import { useEffect, useRef, useState } from 'react';

/**
 * Two-phase mount for a menu/sheet: `mounted` keeps it in the DOM while `isExiting` drives the
 * exit animation. When `open` flips false we animate first, then unmount after `exitMs`.
 * Extracted from ActionMenu.js (500-line cap), zero behaviour change.
 */
export default function useExitPhaseMount(open, exitMs) {
  const [mounted, setMounted] = useState(open);
  const [isExiting, setIsExiting] = useState(false);
  const exitTimerRef = useRef(null);

  useEffect(() => {
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }
    if (open) {
      setMounted(true);
      setIsExiting(false);
    } else if (mounted) {
      setIsExiting(true);
      exitTimerRef.current = setTimeout(() => {
        setMounted(false);
        setIsExiting(false);
        exitTimerRef.current = null;
      }, exitMs);
    }
    // intentionally exclude `mounted` from deps — including it would re-run the effect mid-exit
    // (when `setIsExiting(true)` re-renders) and clear the pending unmount timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => () => {
    if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
  }, []);

  return { mounted, isExiting };
}
